import type { FC, ReactNode } from 'react'
import type { CarWashState } from '@/types'
import type { Command } from '@/lib/carwash/engine'
import type { Lookups } from '@/lib/carwash/selectors'
import type { CarWashDataSource } from '@/lib/carwash/data/data-source'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { useToast } from '@/components/kit/toast'
import { CarWashError, applyCommand } from '@/lib/carwash/engine'
import { createLookups } from '@/lib/carwash/selectors'
import { createDemoState, createFreshState } from '@/lib/carwash/seed'
import { StorageFullError, createLocalStorageSource } from '@/lib/carwash/data/local-storage-source'
import { clearPhotos, deletePhoto, isLocalPhoto } from '@/lib/carwash/data/photo-store'

export type CarWashStatus = 'loading' | 'ready' | 'error'

export type RunResult = { ok: true } | { ok: false, error: string }

type CarWashContextValue = {
    status: CarWashStatus
    state: CarWashState | null
    lookups: Lookups | null
    run: (command: Command) => RunResult
    restoreDemo: () => Promise<void>
    startFresh: () => Promise<void>
}

const CarWashContext = createContext<CarWashContextValue | null>(null)

/*
 * Loads the saved car wash data once the page is running in the browser,
 * creates demo data on the very first visit, runs every command through the
 * business rules in the engine, and saves the result through the data
 * source. Nothing here reads browser storage during server rendering.
 */
export const CarWashProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const toast = useToast()
    const toastRef = useRef(toast)
    const sourceRef = useRef<CarWashDataSource | null>(null)
    const stateRef = useRef<CarWashState | null>(null)
    const warnedRef = useRef(false)

    const [state, setState] = useState<CarWashState | null>(null)
    const [status, setStatus] = useState<CarWashStatus>('loading')

    useEffect(() => {
        toastRef.current = toast
    }, [toast])

    const replace = useCallback((next: CarWashState, command: Command | null = null) => {
        stateRef.current = next
        setState(next)

        const source = sourceRef.current
        if (!source) return

        source.commit(command, next)
            .then(() => {
                warnedRef.current = false
            })
            .catch(error => {
                if (warnedRef.current) return
                warnedRef.current = true

                toastRef.current({
                    tone: 'error',
                    title: error instanceof StorageFullError ? 'This device is out of storage space' : 'Could not save on this device',
                    description: 'Your latest change is on screen but may be lost after a refresh.',
                    duration: 8000
                })
            })
    }, [])

    useEffect(() => {
        const source = createLocalStorageSource()
        sourceRef.current = source

        let cancelled = false

        source.load()
            .then(loaded => {
                if (cancelled) return

                if (loaded) {
                    stateRef.current = loaded
                    setState(loaded)
                }
                else {
                    replace(createDemoState(new Date()))
                }

                setStatus('ready')
            })
            .catch(() => {
                if (!cancelled) setStatus('error')
            })

        const stop = source.watch(external => {
            if (!external) return
            stateRef.current = external
            setState(external)
        })

        return () => {
            cancelled = true
            stop()
        }
    }, [replace])

    const run = useCallback((command: Command): RunResult => {
        const current = stateRef.current
        if (!current) return { ok: false, error: 'The app is still loading. Try again in a moment.' }

        try {
            const next = applyCommand(current, command, new Date().toISOString())
            if (next !== current) replace(next, command)
            return { ok: true }
        }
        catch (error) {
            if (error instanceof CarWashError) return { ok: false, error: error.message }

            console.error(error)
            return { ok: false, error: 'Something went wrong. Please try again.' }
        }
    }, [replace])

    const restoreDemo = useCallback(async () => {
        await clearPhotos()
        replace(createDemoState(new Date()))
        setStatus('ready')
    }, [replace])

    const startFresh = useCallback(async () => {
        const base = stateRef.current ?? createDemoState(new Date())
        const keep = new Set<string>()

        for (const member of base.staff) if (member.photo) keep.add(member.photo)
        if (base.settings.logo) keep.add(base.settings.logo)

        const remove = new Set<string>()

        for (const job of base.jobs) if (job.photo && isLocalPhoto(job.photo) && !keep.has(job.photo)) remove.add(job.photo)
        for (const vehicle of base.vehicles) if (vehicle.photo && isLocalPhoto(vehicle.photo) && !keep.has(vehicle.photo)) remove.add(vehicle.photo)

        await Promise.all(Array.from(remove, ref => deletePhoto(ref)))

        replace(createFreshState(base, new Date()))
        setStatus('ready')
    }, [replace])

    const lookups = useMemo(() => state ? createLookups(state) : null, [state])

    const value = useMemo<CarWashContextValue>(() => ({
        status,
        state,
        lookups,
        run,
        restoreDemo,
        startFresh
    }), [status, state, lookups, run, restoreDemo, startFresh])

    return (
        <CarWashContext.Provider value={value}>
            {children}
        </CarWashContext.Provider>
    )
}

export const useCarWash = (): CarWashContextValue => {
    const context = useContext(CarWashContext)
    if (!context) throw new Error('useCarWash must be used inside CarWashProvider')
    return context
}

/* For components rendered inside <DataGate>, where the data is always loaded. */
export const useCarWashData = (): { state: CarWashState, lookups: Lookups } => {
    const { state, lookups } = useCarWash()
    if (!state || !lookups) throw new Error('useCarWashData must be used inside DataGate')
    return { state, lookups }
}
